// src/components/laboratory/tabs/ProductsTab.jsx - Mis à jour avec catégories
import React, { useState } from 'react';
import { Plus, Package, Search, PencilLine, Trash2, Tag, Eye, Filter, Grid, List } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getProductImageUrl } from '../../../utils/imageUrl';
import ProductDetailModal from '../modals/ProductDetailModal';
import { PRODUCT_CATEGORIES, getCategoryLabel, getCategoryIcon } from '../../../constants/product-categories';

const ProductsTab = ({ 
  products, 
  loading, 
  onAddProduct, 
  onEditProduct, 
  onDeleteProduct,
  promotions = [], // Add promotions prop to check if products have promotions
  laboratory,
  adds = [] // Add adds prop for advertisements
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all'); // Nouveau filtre par catégorie
  const [priceFilter, setPriceFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Create a map of productId to promotions for efficient lookup
  const productPromotionsMap = React.useMemo(() => {
    const map = new Map();
    
    if (promotions && promotions.length > 0) {
      promotions.forEach(promotion => {
        if (promotion.products && promotion.products.length > 0) {
          promotion.products.forEach(product => {
            if (!map.has(product.id)) {
              map.set(product.id, []);
            }
            map.get(product.id).push({
              id: promotion.id,
              name: promotion.name,
              status: promotion.status
            });
          });
        }
      });
    }
    
    return map;
  }, [promotions]);

  // Filter products based on search term, category, and price
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    
    let matchesPrice = true;
    if (priceFilter === 'low') {
      matchesPrice = product.price <= 100;
    } else if (priceFilter === 'medium') {
      matchesPrice = product.price > 100 && product.price <= 500;
    } else if (priceFilter === 'high') {
      matchesPrice = product.price > 500;
    }
    
    return matchesSearch && matchesCategory && matchesPrice;
  });

  // Calculate category statistics
  const categoryStats = React.useMemo(() => {
    const stats = {};
    products.forEach(product => {
      if (product.category) {
        stats[product.category] = (stats[product.category] || 0) + 1;
      }
    });
    return stats;
  }, [products]);

  // Handle product row click
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsDetailModalOpen(true);
  };

  // Handle edit button click (prevent row click)
  const handleEditClick = (e, product) => {
    e.stopPropagation();
    onEditProduct(product);
  };

  // Handle delete button click (prevent row click)
  const handleDeleteClick = (e, product) => {
    e.stopPropagation();
    onDeleteProduct(product);
  };

  // Product Card Component for Grid View
  const ProductCard = ({ product }) => {
    const productPromotions = productPromotionsMap.get(product.id) || [];
    const hasActivePromotion = productPromotions.some(p => p.status === 'IN_PROGRESS');
    const productAds = adds.filter(add => add.productId === product.id);

    return (
      <div 
        className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer group"
        onClick={() => handleProductClick(product)}
      >
        {/* Product Image */}
        <div className="relative aspect-square bg-gray-100 rounded-t-lg overflow-hidden">
          {product.image ? (
            <img
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              src={getProductImageUrl(product.image)}
              alt={product.name}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="h-16 w-16 text-gray-400" />
            </div>
          )}
          
          {/* Promotion Badge */}
          {hasActivePromotion && (
            <div className="absolute top-2 left-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <Tag size={12} className="mr-1" />
                Promo
              </span>
            </div>
          )}

          {/* Category Badge */}
          {product.category && (
            <div className="absolute top-2 right-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {getCategoryIcon(product.category)}
              </span>
            </div>
          )}

          {/* Actions Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleProductClick(product);
                }}
                className="p-2 bg-white rounded-full text-gray-700 hover:text-blue-600 transition-colors"
                title="Voir les détails"
              >
                <Eye size={16} />
              </button>
              <button
                onClick={(e) => handleEditClick(e, product)}
                className="p-2 bg-white rounded-full text-gray-700 hover:text-indigo-600 transition-colors"
                title="Modifier"
              >
                <PencilLine size={16} />
              </button>
              <button
                onClick={(e) => handleDeleteClick(e, product)}
                className="p-2 bg-white rounded-full text-gray-700 hover:text-red-600 transition-colors"
                title="Supprimer"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-medium text-gray-900 line-clamp-2 hover:text-blue-600">
              {product.name}
            </h3>
          </div>

          {/* Category and Subcategory */}
          {product.category && (
            <div className="mb-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                {getCategoryIcon(product.category)} {getCategoryLabel(product.category)}
              </span>
              {product.subcategory && (
                <p className="text-xs text-gray-500 mt-1 line-clamp-1">{product.subcategory}</p>
              )}
            </div>
          )}

          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
            {product.description.length > 100
              ? `${product.description.substring(0, 100)}...`
              : product.description}
          </p>

          <div className="flex items-center justify-between mb-3">
            <span className="text-lg font-bold text-green-600">
              {product.price.toFixed(2)} DH
            </span>
            <span className="text-xs text-gray-400">
              {format(new Date(product.createdAt), 'dd/MM/yyyy', { locale: fr })}
            </span>
          </div>

          {/* Display promotions and ads associated with the product */}
          <div className="flex flex-wrap gap-1 mb-3">
            {/* Promotions */}
            {productPromotions.length > 0 && (
              <>
                {productPromotions.slice(0, 2).map(promo => (
                  <span 
                    key={promo.id}
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      promo.status === 'IN_PROGRESS' 
                        ? 'bg-green-100 text-green-800' 
                        : promo.status === 'DRAFT'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {promo.name}
                  </span>
                ))}
                {productPromotions.length > 2 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                    +{productPromotions.length - 2}
                  </span>
                )}
              </>
            )}
            
            {/* Ads */}
            {productAds.length > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                📢 {productAds.length} pub{productAds.length > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-2">
            <button
              onClick={(e) => handleEditClick(e, product)}
              className="flex-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded-md transition-colors"
            >
              Modifier
            </button>
            <button
              onClick={(e) => handleDeleteClick(e, product)}
              className="text-sm bg-red-100 hover:bg-red-200 text-red-700 py-2 px-3 rounded-md transition-colors"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Produits ({filteredProducts.length}/{products.length})</h3>
        <button
          onClick={onAddProduct}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500"
        >
          <Plus className="-ml-0.5 mr-1 h-4 w-4" />
          Nouveau produit
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-3">
        {/* Search bar */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="Rechercher un produit..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filters Row */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Category Filter */}
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Tag className="h-4 w-4 text-gray-400" />
              </div>
              <select
                className="block w-full pl-10 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">Toutes les catégories</option>
                {PRODUCT_CATEGORIES.map(category => {
                  const count = categoryStats[category.key] || 0;
                  return (
                    <option key={category.key} value={category.key}>
                      {category.icon} {category.label} ({count})
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Price Filter */}
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-4 w-4 text-gray-400" />
              </div>
              <select
                className="block w-full pl-10 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500"
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value)}
              >
                <option value="all">Tous les prix</option>
                <option value="low">Moins de 100 DH</option>
                <option value="medium">100 - 500 DH</option>
                <option value="high">Plus de 500 DH</option>
              </select>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex border border-gray-300 rounded-md">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 rounded-l-md text-sm ${
                viewMode === 'grid' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title="Vue grille"
            >
              <Grid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-r-md border-l text-sm ${
                viewMode === 'list' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title="Vue liste"
            >
              <List size={16} />
            </button>
          </div>
        </div>

        {/* Active Filters Display */}
        {(categoryFilter !== 'all' || priceFilter !== 'all' || searchTerm) && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-gray-600">Filtres actifs:</span>
            
            {searchTerm && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Recherche: "{searchTerm}"
                <button
                  onClick={() => setSearchTerm('')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            
            {categoryFilter !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {getCategoryIcon(categoryFilter)} {getCategoryLabel(categoryFilter)}
                <button
                  onClick={() => setCategoryFilter('all')}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            )}
            
            {priceFilter !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Prix: {priceFilter === 'low' ? '< 100 DH' : priceFilter === 'medium' ? '100-500 DH' : '> 500 DH'}
                <button
                  onClick={() => setPriceFilter('all')}
                  className="ml-1 text-yellow-600 hover:text-yellow-800"
                >
                  ×
                </button>
              </span>
            )}
            
            <button
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setPriceFilter('all');
              }}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Effacer tous les filtres
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-700 border-r-transparent"></div>
          <p className="mt-2 text-gray-500">Chargement des produits...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-8 bg-white border border-gray-200 rounded-lg">
          <Package size={40} className="mx-auto text-gray-400 mb-2" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun produit trouvé</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || categoryFilter !== 'all' || priceFilter !== 'all' ? 
              "Aucun produit ne correspond à vos critères de recherche." : 
              "Ce laboratoire n'a pas encore de produits."}
          </p>
          {(searchTerm || categoryFilter !== 'all' || priceFilter !== 'all') ? (
            <button
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setPriceFilter('all');
              }}
              className="mt-3 text-sm text-green-600 hover:text-green-500"
            >
              Réinitialiser les filtres
            </button>
          ) : (
            <button
              onClick={onAddProduct}
              className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            >
              <Plus className="-ml-0.5 mr-2 h-4 w-4" />
              Ajouter le premier produit
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredProducts.map((product) => {
              // Get promotions for this product
              const productPromotions = productPromotionsMap.get(product.id) || [];
              const hasActivePromotion = productPromotions.some(p => p.status === 'IN_PROGRESS');
              
              // Get ads for this product
              const productAds = adds.filter(add => add.productId === product.id);
              
              return (
                <li key={product.id}>
                  <div 
                    className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleProductClick(product)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="flex-shrink-0">
                          {product.image ? (
                            <img
                              className="h-12 w-12 rounded-md object-cover"
                              src={getProductImageUrl(product.image)}
                              alt={product.name}
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-md bg-gray-200 flex items-center justify-center">
                              <Package className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1 px-4">
                          <div>
                            <p className="text-sm font-medium text-indigo-600 truncate">{product.name}</p>
                            
                            {/* Category and subcategory */}
                            {product.category && (
                              <div className="mt-1 flex items-center">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {productPromotions.slice(0, 2).map(promo => (
                                    <span 
                                      key={promo.id}
                                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                        promo.status === 'IN_PROGRESS' 
                                          ? 'bg-green-100 text-green-800' 
                                          : promo.status === 'DRAFT'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-red-100 text-red-800'
                                      }`}
                                    >
                                      {promo.name}
                                    </span>
                                  ))}
                                  {productPromotions.length > 2 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                      +{productPromotions.length - 2}
                                    </span>
                                  )}
                                  </span>
                                </div>
                              )}
                              
                              {/* Ads */}
                              {productAds.length > 0 && (
                                <div className="flex items-center">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    📢 {productAds.length} pub{productAds.length > 1 ? 's' : ''}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div className="flex-shrink-0 mr-6">
                          <p className="text-sm font-semibold text-gray-900">{product.price.toFixed(2)} DH</p>
                          {hasActivePromotion && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                              <Tag size={10} className="mr-1" />
                              Promo active
                            </span>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProductClick(product);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Voir les détails"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={(e) => handleEditClick(e, product)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Modifier"
                          >
                            <PencilLine size={18} />
                          </button>
                          <button
                            onClick={(e) => handleDeleteClick(e, product)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedProduct(null);
          }}
          laboratory={laboratory}
          promotions={promotions}
          adds={adds}
        />
      )}
    </div>
  );
};

export default ProductsTab;